<template>
    <div class="lg:grid lg:grid-cols-12 lg:gap-x-5">
        <ControlsMenuComponent />

        <div class="space-y-6 sm:px-6 lg:col-span-7 lg:px-0">
            <div class="bg-white shadow-sm sm:rounded-lg">
                <div class="px-4 py-5 sm:p-6">
                    <div class="sm:inline-flex items-center justify-between w-full">
                        <h3 class="text-base font-semibold text-gray-900">{{ $t('subscription.title') }}</h3>
                        <span class="text-sm text-gray-500" v-if="subscription.key !== 'free'">{{ $t('subscription.active_until') }} <span class="font-medium">{{ subscribedTo }}</span></span>
                    </div>
                    <div class="mt-2 sm:flex sm:items-start sm:justify-between w-full">
                        <div class="w-full text-sm text-gray-500">
                            <div class="w-full bg-gray-200 h-2 rounded">
                                <div
                                    :class="{
                                      'bg-yellow-500': storageUsedPercent >= 90 && storageUsedPercent < 100,
                                      'bg-red-500': storageUsedPercent >= 100,
                                      'bg-blue-600': storageUsedPercent < 90
                                    }"
                                    :style="{ width: `${Math.min(storageUsedPercent, 100)}%` }"
                                    class="h-full rounded"
                                ></div>
                            </div>

                            <div class="flex justify-between items-center mt-2">
                                <span>{{ $t('subscription.used') }}:</span>
                                <span><strong>{{ storageUsed }}</strong> / {{ storageLimit }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="border-l-4 border-yellow-400 bg-yellow-50 p-4 mt-3" v-if="storageUsedPercent >= 90 && storageUsedPercent < 100">
                        <div class="flex">
                            <div class="shrink-0">
                                <ExclamationTriangleIcon class="size-5 text-yellow-400" aria-hidden="true" />
                            </div>
                            <div class="ml-3">
                                <p class="text-sm text-yellow-700">
                                    {{ $t('subscription.storage_warning') }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="border-l-4 border-red-400 bg-red-50 p-4 mt-3" v-if="storageUsedPercent >= 100">
                        <div class="flex">
                            <div class="shrink-0">
                                <XCircleIcon class="size-5 text-red-400" aria-hidden="true" />
                            </div>
                            <div class="ml-3">
                                <p class="text-sm text-red-700">
                                    {{ $t('subscription.storage_alert') }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="rounded-md bg-blue-50 p-4 mt-3" v-if="subscription.key === 'free'">
                        <div class="flex">
                            <div class="shrink-0">
                                <InformationCircleIcon class="size-5 text-royal-blue-400" aria-hidden="true" />
                            </div>
                            <div class="ml-3 flex-1 md:flex md:justify-between">
                                <p class="text-sm text-royal-blue-500">
                                    {{ $t('subscription.storage_info') }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <section aria-labelledby="plan-heading">
                <div>
                    <!-- Black Friday Upgrade Banner -->
                    <div class="border-l-4 border-purple-500 bg-gradient-to-r from-purple-50 to-pink-50 p-4 mb-4 shadow-sm rounded-lg" v-if="hasBlackFridayUpgrades">
                        <div class="flex">
                            <div class="shrink-0">
                                <span class="text-2xl">🛍️</span>
                            </div>
                            <div class="ml-3 flex-1">
                                <p class="text-sm font-bold text-purple-900">
                                    ⚡ {{ $t('subscription.black_friday.banner_title') }}
                                </p>
                                <p class="text-sm text-purple-800 mt-1">
                                    {{ $t('subscription.black_friday.banner_description') }}
                                    <a :href="blackFridayTermsUrl" target="_blank" class="ml-2 text-sm font-medium text-purple-700 hover:text-purple-900 hover:underline transition-all cursor-pointer">
                                        {{ $t('subscription.black_friday.terms_button') }}
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="shadow-sm sm:overflow-hidden sm:rounded-md">
                        <div class="space-y-6 bg-white px-4 py-6 sm:p-6">
                            <div class="md:inline-flex justify-between items-center w-full">
                                <h2 id="plan-heading" class="text-lg/6 font-medium text-gray-900">{{ $t('subscription.change_plan') }}</h2>
                                <SwitchGroup as="div" class="flex items-center">
                                    <SwitchLabel as="span" class="mr-3 text-sm">
                                        <span class="font-medium text-gray-900">{{ $t('subscription.yearly_subscription') }}</span>
                                        {{ ' ' }}
                                        <span class="text-gray-500">{{ $t('subscription.save_up_to') }}</span>
                                    </SwitchLabel>
                                    <Switch v-model="annualBillingEnabled" :class="[annualBillingEnabled ? 'bg-royal-blue-500' : 'bg-gray-200', 'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-royal-blue-700 focus:ring-offset-2 focus:outline-hidden']">
                                        <span aria-hidden="true" :class="[annualBillingEnabled ? 'translate-x-5' : 'translate-x-0', 'inline-block size-5 transform rounded-full bg-white ring-0 shadow-sm transition duration-200 ease-in-out']" />
                                    </Switch>
                                </SwitchGroup>
                            </div>

                            <fieldset :aria-label="$t('subscription.plans')">
                                <RadioGroup v-model="selected" class="space-y-4">
                                    <RadioGroupOption
                                        as="template"
                                        v-for="(item, index) in available"
                                        :key="`subscription-plan-radio-item-${index}-${item.key}`"
                                        :value="item"
                                        :aria-label="item.title"
                                        :disabled="item.key === 'free'"
                                        :aria-description="`${item.title}, ${Math.round(item.limit / (1024 * 1024 * 1024) * 100) / 100} ГБ сховища, ${getPriceForCurrency(item)} ${getCurrencySymbol()} на місяць`"
                                        v-slot="{ active, checked }">
                                        <div>
                                            <SubscriptionPlanComponent
                                                :key="`subscription-plan-item-radio-${index}-${item.key}`"
                                                :item="item"
                                                :period="period"
                                                :subscription="subscription"
                                                :selected="selected"
                                                :active="active"
                                                :checked="checked"
                                                :black-friday-upgrade="blackFridayUpgrades[item.key]"
                                            />
                                        </div>
                                    </RadioGroupOption>
                                </RadioGroup>
                            </fieldset>
                        </div>
                        <div class="bg-gray-50 px-4 py-3 text-right sm:px-6 inline-flex justify-between w-full">
                            <router-link
                                :to="subscription.key == selected?.key && period == 1 ? '#' : `/checkout/${selected?.key || ''}?p=${period}${selected && blackFridayUpgrades[selected.key] && blackFridayUpgrades[selected.key].can_upgrade && period === 1 ? '&black_friday=true' : ''}`"
                                :disabled="subscription.key == selected?.key && period == 1"
                                :class="{
                                    'ml-auto inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-xs disabled:bg-royal-blue-300 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900': true,
                                    'bg-royal-blue-500 cursor-pointer hover:bg-royal-blue-600 ': !(subscription.key == selected?.key && period == 1),
                                    'bg-royal-blue-300 cursor-not-allowed': subscription.key == selected?.key && period == 1
                                }">
                                {{ $t('subscription.choose_and_pay') }}
                            </router-link>
                        </div>
                    </div>
                </div>
            </section>

            <CardListComponent />

            <div class="bg-white shadow-sm sm:rounded-lg">
                <SwitchGroup as="div" class="px-4 py-5 sm:p-6">
                    <SwitchLabel as="h3" class="text-base font-semibold text-gray-900" passive>{{ $t('subscription.auto_renew') }}</SwitchLabel>
                    <div class="mt-2 sm:flex sm:items-start sm:justify-between">
                        <div class="max-w-xl text-sm text-gray-500">
                            <SwitchDescription>{{ $t('subscription.auto_renew_tip') }}</SwitchDescription>
                        </div>
                        <div class="mt-5 sm:mt-0 sm:ml-6 sm:flex sm:shrink-0 sm:items-center">
                            <Switch v-model="renewEnabled" :class="[renewEnabled ? 'bg-royal-blue-500' : 'bg-gray-200', 'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-royal-blue-700 focus:ring-offset-2 focus:outline-hidden']">
                                <span aria-hidden="true" :class="[renewEnabled ? 'translate-x-5' : 'translate-x-0', 'inline-block size-5 transform rounded-full bg-white ring-0 shadow-sm transition duration-200 ease-in-out']" />
                            </Switch>
                        </div>
                    </div>
                </SwitchGroup>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {defineComponent} from "vue";

import {Subscription, SubscriptionPlan} from "@/types/UserType";

import {checkBlackFridayUpgrade, disableRenew, enableRenew, plans, subscription} from "@/data/requests/subscription";
import {mapActions, mapGetters} from "vuex";
import {formatStorage} from "@/_helpers/formatters";
import moment from "moment";
import SubscriptionPlanComponent from "@/components/Subscription/SubscriptionPlanComponent.vue";
import CardListComponent from "@/components/Subscription/CardListComponent.vue";
import ControlsMenuComponent from "@/components/Users/ControlsMenuComponent.vue";
import { Switch, SwitchDescription, SwitchGroup, SwitchLabel, RadioGroup, RadioGroupOption } from '@headlessui/vue'
import {ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon} from "@heroicons/vue/20/solid";

export default defineComponent({
    name: "SubscriptionPage",

    components: {
        InformationCircleIcon,
        ExclamationTriangleIcon,
        XCircleIcon,
        ControlsMenuComponent,
        SubscriptionPlanComponent,
        CardListComponent,
        Switch,
        SwitchDescription,
        SwitchGroup,
        SwitchLabel,

        RadioGroup, RadioGroupOption
    },

    data() {
        return {
            subscription: {} as Subscription,

            available: [] as SubscriptionPlan[],

            selected: undefined as SubscriptionPlan | undefined,

            renewEnabled: false as boolean,

            annualBillingEnabled: false,
            period: 1,

            blackFridayUpgrades: {} as Record<string, any>,
        }
    },

    computed: {
        ...mapGetters({
            user: "user/user",
        }),

        storageUsed(): string {
            return formatStorage(this.user ? this.user.storage.used : 0, this.$t);
        },

        storageLimit(): string {
            return formatStorage(this.user ? this.user.storage.limit : 0, this.$t);
        },

        storageUsedPercent(): number {
            if(!this.user) {
                return 0;
            }

            return (this.user.storage.used / this.user.storage.limit) * 100;
        },

        subscribedTo(): string {
            if(!this.subscription.subscribed_to) {
                return "";
            }

            return moment(this.subscription.subscribed_to).format("DD.MM.YYYY");
        },

        hasBlackFridayUpgrades(): boolean {
            return Object.keys(this.blackFridayUpgrades).length > 0;
        },

        blackFridayTermsUrl(): string {
            const locale = this.$i18n.locale === 'en' ? 'en' : 'uk';
            return locale === 'en' 
                ? 'https://gallera.io/en/blackfriday2025'
                : 'https://gallera.io/blackfriday2025';
        },
    },

    watch: {
        annualBillingEnabled(newValue) {
            this.period = newValue ? 12 : 1;
        },

        renewEnabled(newValue, oldValue) {
            if (oldValue === false && newValue === true) {
                this.enableRenew();
            }

            if (oldValue === true && newValue === false) {
                this.disableRenew();
            }
        },

        available: {
            handler() {
                console.log('SubscriptionPage: available plans changed, checking Black Friday upgrades');
                this.checkBlackFridayUpgrades();
            },
            immediate: true
        },

        user: {
            handler(newUser) {
                if (newUser && this.available.length > 0) {
                    console.log('SubscriptionPage: user loaded, checking Black Friday upgrades');
                    this.checkBlackFridayUpgrades();
                }
            },
            immediate: true
        }
    },

    mounted() {
        console.log('SubscriptionPage: mounted');
        this.fetchDetails();
        this.fetchPlans();
    },

    methods: {
        ...mapActions({
            errorAlert: "alerts/error",
        }),

        fetchDetails() {
            subscription((subscription) => {
                this.subscription = subscription;

                this.selected = this.available.find((plan) => plan.key === subscription.key) || undefined;
                this.renewEnabled = subscription.renew;
            }, () => {
                this.errorAlert({
                    message: this.$t('subscription.fetch_error'),
                    timeout: 30,
                })
            })
        },

        fetchPlans() {
            plans((plans) => {
                this.available = plans;

                this.selected = this.available.find((plan) => plan.key === this.subscription.key) || undefined;
            }, () => {
                this.errorAlert({
                    message: this.$t('subscription.fetch_plans_error'),
                    timeout: 30,
                })
            })
        },

        checkBlackFridayUpgrades() {
            if (!this.user || this.available.length === 0) {
                console.log('SubscriptionPage: checkBlackFridayUpgrades skipped - no user or plans', {
                    user: !!this.user,
                    plansCount: this.available.length
                });
                return;
            }

            console.log('SubscriptionPage: calling checkBlackFridayUpgrade API');
            
            checkBlackFridayUpgrade((data) => {
                console.log('SubscriptionPage: Black Friday upgrade response:', data);
                
                if (data.is_active && data.can_upgrade && data.available_upgrades) {
                    console.log('SubscriptionPage: Found', data.available_upgrades.length, 'available upgrades');
                    
                    // Зберігаємо інформацію про доступні апгрейди
                    data.available_upgrades.forEach((upgrade: any) => {
                        console.log('SubscriptionPage: Setting upgrade for plan:', upgrade.plan_key, upgrade);
                        this.blackFridayUpgrades[upgrade.plan_key] = {
                            can_upgrade: true,
                            old_plan: upgrade.old_plan,
                            new_plan: upgrade.plan,
                            bonus_days: upgrade.bonus_days,
                            discount_percent: upgrade.discount_percent,
                        };
                    });
                    
                    console.log('SubscriptionPage: Final blackFridayUpgrades:', this.blackFridayUpgrades);
                } else {
                    console.log('SubscriptionPage: No upgrades available', {
                        is_active: data.is_active,
                        can_upgrade: data.can_upgrade,
                        has_upgrades: !!data.available_upgrades
                    });
                }
            }, (type, error) => {
                console.error('SubscriptionPage: Black Friday upgrade check error:', type, error);
            });
        },

        disableRenew() {
            disableRenew(() => {
                this.subscription.renew = false;
            }, () => {
                this.errorAlert({
                    message: this.$t('subscription.auto_renew_off_error'),
                    timeout: 30,
                })
            })
        },

        enableRenew() {
            enableRenew(() => {
                this.subscription.renew = true;
            }, () => {
                this.errorAlert({
                    message: this.$t('subscription.auto_renew_on_error'),
                    timeout: 30,
                })
            })
        },

        getPriceForCurrency(item: SubscriptionPlan): string {
            if(item.key === 'free') {
                return '0';
            }
            
            const price = this.user?.currency === 'EUR' ? item.price_eur : item.price;
            // Both EUR and UAH prices are stored in smallest units (cents/kopecks)
            // So we need to divide by 100 for both currencies
            return (price / 100).toString();
        },

        getCurrencySymbol(): string {
            return this.user?.currency === 'EUR' ? this.$t('subscription_plan.eur') : this.$t('subscription_plan.uah');
        },
    }
});
</script>